// engine-status.ts
// =============================================================================
// Shared data layer for the outreach engine surface (CA-010 / CA-023). Kept in
// its own module (not the component file) so EngineStatusBanner.tsx exports ONLY
// a component and satisfies react-refresh/only-export-components — the same split
// convention the ss-deck slides use.
//
// Consumers: EngineStatusBanner, OutreachControl. They all share one cache entry
// (['outreach-engine-status', orgId]) so the badge, banner and control page
// refetch in lock-step and never disagree.
// =============================================================================

import { useQuery } from '@tanstack/react-query'
import { useAuth } from '@/contexts/AuthContext'
import { invokeWithFreshAuth } from '@/lib/edge-functions'

// ─── Engine status contract (matches outreach-engine-status edge fn) ─────────
export interface EngineStatus {
  org_id: string
  cron_exists: boolean
  outreach_enabled: boolean | null
  orphan_scheduled_count: number
  oldest_orphan_at: string | null
  automated_cadences: number
  engine_healthy: boolean
}

interface EngineStatusResponse {
  success: boolean
  statuses: EngineStatus[]
}

// Reads the REAL executor state via outreach-engine-status (CA-003), never
// assumes a running engine.
export function useEngineStatus(orgId: string | null) {
  const { user } = useAuth()
  return useQuery<EngineStatus | null>({
    queryKey: ['outreach-engine-status', orgId],
    queryFn: async () => {
      const { data, error } = await invokeWithFreshAuth<EngineStatusResponse>(
        'outreach-engine-status',
        { body: { orgId }, timeoutMs: 20000 },
      )
      if (error) throw new Error(error.message)
      const statuses = data?.statuses ?? []
      return statuses.find((s) => s.org_id === orgId) ?? statuses[0] ?? null
    },
    enabled: !!user && !!orgId,
    refetchInterval: 60000,
    staleTime: 30000,
  })
}

// Derived, human-readable engine state. The engine is only truly "ON" when the
// kill switch is enabled AND a process-queue cron exists to drain the queue.
export type EngineState = 'on' | 'off' | 'enabled_no_cron' | 'unknown'

export function deriveEngineState(status: EngineStatus | null | undefined): EngineState {
  if (!status) return 'unknown'
  if (status.outreach_enabled !== true) return 'off'
  // Kill switch is on. If no cron drains the queue, sends still won't happen.
  return status.cron_exists ? 'on' : 'enabled_no_cron'
}
